import React from "react";
import AuthorizedPage from "../../models/base/AuthorizedPage";
import {ITimelineRow, PageContainer} from "@renta-apps/athenaeum-react-components";
import {PageCacheProvider, PageRouteProvider} from "@renta-apps/athenaeum-react-common";
import CalendarToolbar from "@/pages/Calendar/Toolbar/CalendarToolbar";
import {Utility} from "@renta-apps/athenaeum-toolkit";
import ConstructionSite from "@/models/server/ConstructionSite";
import BaseCalendarDataRequest from "@/pages/Calendar/BaseCalendarDataRequest";
import CalendarData from "@/pages/Calendar/CalendarData/CalendarData";
import WorkOrderModel from "@/models/server/WorkOrderModel";
import {CalendarUnitTime} from "@/models/Enums";
import GetCalendarWorkOrdersRequest from "@/models/server/requests/GetCalendarWorkOrdersRequest";
import CalendarFiltersData from "./Toolbar/CalendarFiltersData";
import PageDefinitions from "@/providers/PageDefinitions";
import User from "@/models/server/User";
import UpdateMountersAndTimesRequest from "@/models/server/requests/UpdateMountersAndTimesRequest";
import UserInteractionDataStorage from "@/providers/UserInteractionDataStorage";
import UnleashHelper from "@/helpers/UnleashHelper";
import FeatureFlags from "@/helpers/FeatureFlags";
import Subcontractor from "@/models/server/Subcontractor";
import GetUsersRequest from "@/models/server/requests/GetUsersRequest";
import GetCalendarAbsenceAvailabilityRequest from "@/models/server/requests/GetCalendarAbsenceAvailabilityRequest";
import CalendarAbsenceAvailabilityItem from "@/models/server/CalendarAbsenceAvailabilityItem";

import styles from "./Calendar.module.scss";
import "./BootstrapOverride.scss";

interface ICalendarProps {
}

interface ICalendarState {
    filtersData: CalendarFiltersData;
    request: BaseCalendarDataRequest;
    constructionSites: ConstructionSite[];
    tasks: WorkOrderModel[];
    isTeams: boolean;
    rows: readonly Readonly<ITimelineRow>[]
    startDate: Date;
    endDate: Date;

    /** All (non-deleted) sub contractors. */
    subContractors: Subcontractor[];
    userIdsForRedirect: string[];
    absenceAvailability: CalendarAbsenceAvailabilityItem[];
}

export default class Calendar extends AuthorizedPage<ICalendarProps, ICalendarState> {

    // Fields

    public state: ICalendarState = {
        filtersData: new CalendarFiltersData(),
        request: new BaseCalendarDataRequest(),
        constructionSites: [],
        tasks: [],
        isTeams: false,
        startDate: Utility.getFirstDayOfWeek(),
        endDate: Utility.getLastDayOfWeek(),
        rows: [],
        subContractors: [],
        userIdsForRedirect: [],
        absenceAvailability: [],
    };

    private readonly _calendarDataRef: React.RefObject<CalendarData> = React.createRef();

    public static readonly snapSeconds: number = 60 * 60;
    public static readonly snapSecondsDailyView: number = 60 * 15;

    /**
     *  Note: update storage id's version number if stored model has new fields or other changes.
     *  E.g. new fields will be received as undefined for existing values from storage.
     */
    private readonly _requestStorageKey: string = "task.request.v3";

    // Properties

    private get request(): BaseCalendarDataRequest {
        return this.state.request;
    }

    private get filtersData(): CalendarFiltersData {
        return this.state.filtersData;
    }

    private get tasks(): WorkOrderModel[] {
        return this.state.tasks;
    }

    private get mounters(): User[] {
        return this.filtersData.mounters;
    }

    private get selectedMounters(): User[] {
        return this.request.mounters;
    }

    private get selectedCalendarTimeUnit(): CalendarUnitTime {
        return this.request.calendarUnitType;
    }

    private get startDate(): Date {
        return this.state.startDate;
    }

    private get endDate(): Date {
        return this.state.endDate;
    }
    
    private get snapSeconds(): number {
        return this.selectedCalendarTimeUnit === CalendarUnitTime.Day ? Calendar.snapSecondsDailyView : Calendar.snapSeconds;
    }

    // Methods

    private async getTasksAsync(): Promise<void> {
        const request: GetCalendarWorkOrdersRequest = {
            warehouseIds: this.request.warehouses.map(warehouse => warehouse.id),
            constructionSiteIds: this.request.constructionSites.map(constructionSite => constructionSite.id),
            workOrderStatuses: this.request.statuses,
            contractTypes: this.request.contractTypes,
            mounterCostPoolIds: UnleashHelper.isEnabled(FeatureFlags.CostPoolMounterAutoSelect)
                ? [] // Use only mounter filter
                : this.request.mounterCostPools.map(costPool => costPool.id),
            search: this.request.search,
            mounterIds: this.request.mounters.map(user => user.id),
            managerIds: this.request.managers.map(user => user.id),
            regionIds: this.request.regions?.map(region => region.id) ?? [],
            viewStart: this.startDate,
            viewEnd: this.endDate,
        };

        const tasks: WorkOrderModel[] = await this.postAsync("api/calendar/getWorkOrders", request);
        await this.setState({tasks});
        await this.loadAbsenceAvailabilityAsync();
    }

    private async loadAbsenceAvailabilityAsync(): Promise<void> {
        if (!UnleashHelper.isEnabled(FeatureFlags.AbsenceFields) || !UnleashHelper.isEnabled(FeatureFlags.AbsenceManagement)) {
            await this.setState({ absenceAvailability: [] });
            return;
        }

        const mounterIds: string[] = (this.selectedMounters.length > 0 ? this.selectedMounters : this.mounters).map(m => m.id);
        if (mounterIds.length === 0) {
            await this.setState({ absenceAvailability: [] });
            return;
        }

        const request: GetCalendarAbsenceAvailabilityRequest = {
            mounterIds,
            viewStart: this.startDate,
            viewEnd: this.endDate,
        };

        const absenceAvailability: CalendarAbsenceAvailabilityItem[] = await this.postAsync("api/calendar/getCalendarAbsenceAvailability", request);
        await this.setState({ absenceAvailability });
    }
    

    private async changeDataAsync(type: "prev" | "next" | "today"): Promise<void> {
        let startDate: Date | null = null;
        let endDate: Date | null = null;
        switch (type) {
            case "prev":
                if (this.selectedCalendarTimeUnit === CalendarUnitTime.Week) {
                    startDate = this.startDate.addDays(-7);
                    endDate = this.endDate.addDays(-7);
                } else if(this.selectedCalendarTimeUnit === CalendarUnitTime.TwoWeeks){
                    startDate = this.startDate.addDays(-14);
                    endDate = this.endDate.addDays(-14);
                } else if (this.selectedCalendarTimeUnit === CalendarUnitTime.Month) {
                    startDate = this.startDate.addMonths(-1).getFirstDayOfMonth();
                    endDate = this.endDate.addMonths(-2).getLastDayOfMonth().addDays(1);
                } else if (this.selectedCalendarTimeUnit === CalendarUnitTime.Day) {
                    startDate = this.startDate.addDays(-1);
                    endDate = this.endDate.addDays(-1);
                } 
                break;
            case "next":
                if (this.selectedCalendarTimeUnit === CalendarUnitTime.Week) {
                    startDate = this.startDate.addDays(7);
                    endDate = this.endDate.addDays(7);
                } else if (this.selectedCalendarTimeUnit === CalendarUnitTime.TwoWeeks) {
                    startDate = this.startDate.addDays(14);
                    endDate = this.endDate.addDays(14);
                } else if (this.selectedCalendarTimeUnit === CalendarUnitTime.Month) {
                    startDate = this.startDate.addMonths(1).getFirstDayOfMonth();
                    endDate = this.endDate.getLastDayOfMonth().addDays(1);
                } else if (this.selectedCalendarTimeUnit === CalendarUnitTime.Day) {
                    startDate = this.startDate.addDays(1);
                    endDate = this.endDate.addDays(1);
                }
                break;
            case "today":
                if (this.selectedCalendarTimeUnit === CalendarUnitTime.Week) {
                    startDate = Utility.getFirstDayOfWeek();
                    endDate = Utility.getLastDayOfWeek().addDays(1);
                } else if (this.selectedCalendarTimeUnit === CalendarUnitTime.TwoWeeks) {
                    const firstDayOfMonth = Utility.getFirstDayOfMonth();
                    const dayOfWeek = firstDayOfMonth.getDay();
                    startDate = firstDayOfMonth.addDays(-(dayOfWeek === 0 ? 6 : dayOfWeek - 1));
                    endDate = startDate.addDays(14);
                } else if (this.selectedCalendarTimeUnit === CalendarUnitTime.Month) {
                    startDate = Utility.getFirstDayOfMonth();
                    endDate = Utility.getLastDayOfMonth().addDays(1);
                } else if (this.selectedCalendarTimeUnit === CalendarUnitTime.Day) {
                    startDate = Utility.today();
                    endDate = Utility.today().addDays(1);
                    endDate.setHours(endDate.getHours() + 8);
                }         
                break;
        }
        
        if (!startDate || !endDate) {
            throw new Error("Configuration missing for time unit " + this.selectedCalendarTimeUnit);
        }

        await this.setState({
            startDate: startDate!,
            endDate: endDate!,
        });
    }

    private async onFiltersChangeAsync(request: BaseCalendarDataRequest, type: "prev" | "next" | "today" | null): Promise<void> {
        UserInteractionDataStorage.setFilters(request, this._requestStorageKey);
        await this.setState({request});
        
        if (type) {
            await this.changeDataAsync(type);
        }

        await this.getTasksAsync();
        
    }

    private async refreshFilterData(): Promise<void> {
        const filters: CalendarFiltersData = await PageCacheProvider.getAsync("getCalendarFiltersData",
            () => this.postAsync("api/calendar/getCalendarFiltersData"));

        await this.setState({filtersData: filters});
    }

    private readonly onWorkOrderChangeAsync = async (request: UpdateMountersAndTimesRequest, subContractorChanged: boolean): Promise<void> => {
        await this.postAsync("api/calendar/updateMountersAndTimes", request);
        await this.getTasksAsync();

        if (subContractorChanged) {
            const subContractors: Subcontractor[] = await this.postAsync("api/calendar/getAllSubContractors");
            await this.setState({subContractors: subContractors});
        }
    }

    public async initializeAsync(): Promise<void> {
        await super.initializeAsync();

        const request: BaseCalendarDataRequest = UserInteractionDataStorage.getFilters(new BaseCalendarDataRequest(), this._requestStorageKey)
        const subContractors: Subcontractor[] = await this.postAsync("api/calendar/getAllSubContractors");

        const isRedirectEnabled: boolean = UnleashHelper.isEnabled(FeatureFlags.CalendarRedirectToUserManagement);

        if (isRedirectEnabled) {
            const getUsersRequest = new GetUsersRequest();
            const users: User[] = await this.postAsync("api/admin/getUsers", getUsersRequest);
            const userIdsForRedirect: string[] = users.map(user => user.id);
            await this.setState({userIdsForRedirect});
        }

        await this.setState({
            request: request,
            subContractors: subContractors,
        });

        await this.refreshFilterData();
        await this.changeDataAsync("today");
        await this.getTasksAsync();
    }

    public render(): React.ReactNode {
        return (
            <PageContainer scale
                           className={styles.calendar}
            >

                <div className={styles.container}>

                    <div id="calendarPanelRight"
                         className={styles.rightPanel}>
                        <div>
                            <CalendarToolbar filtersData={this.filtersData}
                                             request={this.request}
                                             startDate={this.startDate}
                                             onAddNewWorkOrder={async () => {await PageRouteProvider.redirectAsync(PageDefinitions.workOrderRoute())}}
                                             onChange={(request: BaseCalendarDataRequest, type: "prev" | "next" | "today" | null) => this.onFiltersChangeAsync(request, type)}
                            />

                            <CalendarData id={"calendarData"}
                                          ref={this._calendarDataRef}
                                          tasks={this.tasks}
                                          mounters={this.mounters}
                                          selectedMounters={this.selectedMounters}
                                          startDate={this.startDate}
                                          endDate={this.endDate}
                                          onWorkOrderChange={this.onWorkOrderChangeAsync}
                                          subContractors={this.state.subContractors}
                                          snapSeconds={this.snapSeconds}
                                          userIdsForRedirect={this.state.userIdsForRedirect}
                                          absenceAvailability={this.state.absenceAvailability}
                            />
                        </div>
                    </div>
                </div>
            </PageContainer>
        );
    }
}